import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useFormFlow, useKeyCap, useRefsManager } from "helper/customHook";

import { DialogActions } from "@mui/material";

import {
  TwoFECode,
  TwoFECodeLabel,
  CodeInputWrapper,
  CodeInput,
  ButtonCustom,
} from "../Modal2FA/style";

import { isOpenDialogSelector } from "redux-store/static/redux/selector";
import { DIALOG } from "helper/enum/dialogEnum";

/** 6 ô OTP — ref keys cho useFormFlow (giống Modal2FA) */
const formStepFields = ["0", "1", "2", "3", "4", "5"];

const EMPTY = "";

function displayCell(raw: string): string {
  return raw === EMPTY ? "-" : raw;
}

export type SixDigitOtpFieldsProps = {
  submitting: boolean;
  onSubmitOtp: (otp: string) => void | Promise<void>;
  label?: string;
  confirmLabel?: string;
};

/** 6 ô OTP (dùng tab “Đã có app” và tab “Cài đặt” sau QR) */
function SixDigitOtpFieldsInner({
  submitting,
  onSubmitOtp,
  label = "Nhập mã xác thực 6 số",
  confirmLabel,
}: SixDigitOtpFieldsProps) {
  const open = useSelector(isOpenDialogSelector(DIALOG.MODAL_VERIFY_INCOME));
  const [code, setCode] = useState<string[]>(() => Array(6).fill(EMPTY));
  const codeRef = useRef(code);
  codeRef.current = code;

  const { onEnter, onNumberOnly, onBackspace, onArrowLeft, onArrowRight } =
    useKeyCap();
  const { attachRef, elementsRef } = useRefsManager();

  const submitWithCurrentCode = useCallback(() => {
    void onSubmitOtp(codeRef.current.join(""));
  }, [onSubmitOtp]);

  const {
    handleStart,
    handleNext,
    handleBack,
    handleNextLoop,
    handleBackLoop,
  } = useFormFlow(elementsRef, formStepFields, submitWithCurrentCode);

  const handleStartRef = useRef(handleStart);
  handleStartRef.current = handleStart;

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => handleStartRef.current()(), 200);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setCode(Array(6).fill(EMPTY));
    }
  }, [open]);

  const handleOtpKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: string
  ) => {
    const i = Number(index);
    onArrowRight(handleNextLoop(index))(e);
    onArrowLeft(handleBackLoop(index))(e);
    onBackspace(() => {
      setCode((prev) => {
        const next = [...prev];
        next[i] = EMPTY;
        return next;
      });
      handleBack(index)();
    })(e);
    onNumberOnly((event: KeyboardEvent) => {
      event.preventDefault();
      setCode((prev) => {
        const next = [...prev];
        next[i] = event.key;
        return next;
      });
      if (i < 5) handleNext(index)();
    })(e);
    onEnter(() => {
      const c = codeRef.current;
      if (c.every((d) => /^[0-9]$/.test(d))) {
        void onSubmitOtp(c.join(""));
      }
    })(e);
  };

  const otpFilled = code.every((d) => /^[0-9]$/.test(d));
  const confirmDisabled = submitting || !otpFilled;
  const btnText = submitting
    ? "Đang xác thực..."
    : (confirmLabel ?? "Xác nhận");

  return (
    <>
      <TwoFECode sx={{ width: 500, maxWidth: "100%" }}>
        <TwoFECodeLabel>{label}</TwoFECodeLabel>
        <CodeInputWrapper>
          {code.map((digit, index) => (
            <CodeInput
              key={index}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={displayCell(digit)}
              onKeyDown={(e) => handleOtpKeyDown(e, `${index}`)}
              disabled={submitting}
              ref={(el: HTMLInputElement) => attachRef(`${index}`)(el)}
            />
          ))}
        </CodeInputWrapper>
      </TwoFECode>
      <DialogActions sx={{ px: 0, pt: 1 }}>
        <ButtonCustom
          disabled={confirmDisabled}
          onClick={() => void onSubmitOtp(code.join(""))}
        >
          {btnText}
        </ButtonCustom>
      </DialogActions>
    </>
  );
}

export const SixDigitOtpFields = memo(SixDigitOtpFieldsInner);

export type TabHasAppProps = {
  submitting: boolean;
  onSubmitOtp: (otp: string) => void | Promise<void>;
};

const TabHasApp = memo(function TabHasApp({
  submitting,
  onSubmitOtp,
}: TabHasAppProps) {
  return (
    <SixDigitOtpFields submitting={submitting} onSubmitOtp={onSubmitOtp} />
  );
});

export default TabHasApp;
